import type { SignedOutReason } from '@novachat/client';
import { ApiError } from '@novachat/client';
import { ErrorCode } from '@novachat/shared';
import { ChevronLeft, Lock, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent } from 'react';
import { Logo, Wordmark } from '../components/Logo';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';

type Step =
  | { name: 'credentials' }
  | { name: 'code'; challengeToken: string; method: 'sms' | 'totp'; mobileMasked: string | null }
  | { name: 'no-access' };

const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;

const reasonText: Partial<Record<SignedOutReason, string>> = {
  expired: 'Oturumun sona erdi. Tekrar giriş yap.',
};

export function Login({ reason }: { reason: SignedOutReason }) {
  const [step, setStep] = useState<Step>(reason === 'no-access' ? { name: 'no-access' } : { name: 'credentials' });

  return (
    <div className="flex min-h-full flex-col items-center gap-7 bg-[radial-gradient(ellipse_60%_42%_at_50%_0%,rgb(89_90_186/0.18)_0%,rgb(255_0_138/0.05)_50%,transparent_80%)] px-4 pt-[max(40px,env(safe-area-inset-top))] pb-[max(32px,env(safe-area-inset-bottom))] sm:gap-9 sm:pt-14">
      <header className="flex flex-col items-center gap-3 text-center">
        <Logo size={56} />
        <h1 className="m-0 text-[32px] leading-tight">
          <Wordmark />
        </h1>
        <p className="m-0 text-[15px] text-fg-2">Novadan ekibinin sohbet ve ses uygulaması</p>
      </header>

      {step.name === 'credentials' && (
        <Credentials notice={reasonText[reason]} onTwoFactor={(s) => setStep({ name: 'code', ...s })} onNoAccess={() => setStep({ name: 'no-access' })} />
      )}
      {step.name === 'code' && (
        <Code step={step} onBack={() => setStep({ name: 'credentials' })} onNoAccess={() => setStep({ name: 'no-access' })} />
      )}
      {step.name === 'no-access' && <NoAccess onRetry={() => setStep({ name: 'credentials' })} />}

      <p className="mt-auto text-xs text-fg-3">Giriş Novadan hesabıyla yapılır. NovaChat ayrı bir hesap istemez.</p>
    </div>
  );
}

function Card({ children, live = false, onSubmit }: { children: React.ReactNode; live?: boolean; onSubmit?: (e: FormEvent) => void }) {
  const className = `flex w-full max-w-[390px] animate-enter flex-col gap-4 rounded-xl p-6 sm:p-7 ${live ? 'nc-grad-border' : 'border border-line bg-surface-1'}`;
  return onSubmit ? (
    <form className={className} style={live ? { ['--nc-fill' as string]: 'var(--color-surface-1)' } : undefined} onSubmit={onSubmit} noValidate>
      {children}
    </form>
  ) : (
    <div className={className}>{children}</div>
  );
}

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="font-heading text-[11px] font-bold tracking-[0.08em] text-fg-3">{children}</span>
);

function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="m-0 animate-shake rounded-md border border-red-400/40 bg-red-400/10 px-3 py-2 text-[13.5px] text-fg-1">
      {message}
    </p>
  );
}

// iOS Safari zooms into inputs smaller than 16px, so phones get 16px.
const inputClass =
  'h-[46px] rounded-md border border-line-strong bg-surface-2 px-3.5 text-base sm:text-[15px] placeholder:text-fg-3 focus:border-brand-400 focus:shadow-[0_0_0_3px_rgb(237_139_93/0.22)] focus:outline-none';
const primaryButton =
  'nc-press nc-cta mt-1 h-12 rounded-md bg-brand-500 text-[15px] font-bold text-white disabled:opacity-60';

function Credentials({
  notice,
  onTwoFactor,
  onNoAccess,
}: {
  notice: string | undefined;
  onTwoFactor: (step: { challengeToken: string; method: 'sms' | 'totp'; mobileMasked: string | null }) => void;
  onNoAccess: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(notice ?? null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!email.trim() || !password) return setError('E-posta ve şifreni gir.');
    setBusy(true);
    setError(null);
    try {
      const result = await client.session.login(email.trim(), password);
      if (result.status === 'two_factor') onTwoFactor(result);
    } catch (e) {
      if (e instanceof ApiError && e.code === ErrorCode.Forbidden) return onNoAccess();
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card onSubmit={submit}>
      <Eyebrow>ADIM 1 · GİRİŞ</Eyebrow>
      <div>
        <h2 className="mb-1.5 font-heading text-[22px] font-bold">Novadan hesabınla gir</h2>
        <p className="m-0 text-sm leading-normal text-fg-2">Novadan admin panelindeki e-posta ve şifreni kullan.</p>
      </div>
      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-semibold text-fg-2">E-posta</span>
        <input className={inputClass} type="email" autoComplete="username" placeholder="ad@novadan.com.tr" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-semibold text-fg-2">Şifre</span>
        <input className={inputClass} type="password" autoComplete="current-password" placeholder="Şifren" value={password} onChange={(e) => setPassword(e.target.value)} />
      </label>
      <ErrorLine message={error} />
      <button type="submit" className={primaryButton} disabled={busy}>
        {busy ? 'Kontrol ediliyor…' : 'Devam et'}
      </button>
      <p className="mt-auto mb-0 flex items-start gap-2 text-[12.5px] leading-normal text-fg-3">
        <Lock size={15} className="mt-px flex-none" aria-hidden="true" />
        Şifren NovaChat’te saklanmaz, sadece doğrulama için Novadan’a iletilir.
      </p>
    </Card>
  );
}

function Code({
  step,
  onBack,
  onNoAccess,
}: {
  step: Extract<Step, { name: 'code' }>;
  onBack: () => void;
  onNoAccess: () => void;
}) {
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(step.method === 'sms' ? RESEND_SECONDS : 0);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function verify(code: string) {
    setBusy(true);
    setError(null);
    try {
      await client.session.verifyTwoFactor(step.challengeToken, code);
    } catch (e) {
      if (e instanceof ApiError && e.code === ErrorCode.Forbidden) return onNoAccess();
      setError(errorMessage(e));
      setDigits(Array(CODE_LENGTH).fill(''));
      inputs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  }

  function fill(index: number, value: string) {
    const typed = value.replace(/\D/g, '');
    const next = [...digits];
    for (let i = 0; i < typed.length && index + i < CODE_LENGTH; i++) next[index + i] = typed[i]!;
    if (!typed) next[index] = '';
    setDigits(next);
    const target = Math.min(index + Math.max(typed.length, 0), CODE_LENGTH - 1);
    if (typed) inputs.current[target]?.focus();
    if (next.every(Boolean)) void verify(next.join(''));
  }

  function onKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && !digits[index] && index > 0) inputs.current[index - 1]?.focus();
  }

  function onPaste(index: number, event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    fill(index, event.clipboardData.getData('text'));
  }

  async function resend() {
    setError(null);
    try {
      await client.session.resendTwoFactor(step.challengeToken);
      setCooldown(RESEND_SECONDS);
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <Card live onSubmit={(e) => (e.preventDefault(), digits.every(Boolean) && void verify(digits.join('')))}>
      <Eyebrow>ADIM 2 · İKİ ADIMLI DOĞRULAMA</Eyebrow>
      <div className="flex items-start gap-3.5">
        <span className="grid size-11 flex-none place-items-center rounded-[13px] bg-brand-500/16 text-brand-400">
          <ShieldCheck size={22} aria-hidden="true" />
        </span>
        <div>
          <h2 className="mb-1.5 font-heading text-[22px] font-bold">Doğrulama kodu</h2>
          <p className="m-0 text-sm leading-normal text-fg-2">
            {step.method === 'sms'
              ? `${step.mobileMasked ?? 'Telefonuna'} numarasına gönderilen 6 haneli kodu gir.`
              : 'Doğrulama uygulamandaki 6 haneli kodu gir.'}
          </p>
        </div>
      </div>
      <fieldset className="m-0 mt-1 border-0 p-0" disabled={busy}>
        <legend className="sr-only">Doğrulama kodu</legend>
        <div className="grid grid-cols-6 gap-2">
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                inputs.current[i] = el;
              }}
              aria-label={`${i + 1}. hane`}
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={i === 0 ? CODE_LENGTH : 1}
              value={digit}
              autoFocus={i === 0}
              onChange={(e) => fill(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              onPaste={(e) => onPaste(i, e)}
              className="h-[54px] min-w-0 rounded-md border border-line-strong bg-surface-2 text-center font-mono text-[22px] font-medium focus:border-brand-400 focus:shadow-[0_0_0_3px_rgb(237_139_93/0.22)] focus:outline-none"
            />
          ))}
        </div>
      </fieldset>
      <ErrorLine message={error} />
      <button type="submit" className={primaryButton} disabled={busy || !digits.every(Boolean)}>
        {busy ? 'Doğrulanıyor…' : 'Doğrula ve gir'}
      </button>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 text-[13px]">
        <button type="button" onClick={onBack} className="flex items-center gap-1 border-0 bg-transparent p-0 text-fg-2 hover:text-fg-1">
          <ChevronLeft size={16} aria-hidden="true" />
          Geri
        </button>
        {step.method === 'sms' &&
          (cooldown > 0 ? (
            <span className="text-fg-3">
              Kodu tekrar gönder <span className="font-mono text-fg-2">0:{String(cooldown).padStart(2, '0')}</span>
            </span>
          ) : (
            <button type="button" onClick={resend} className="border-0 bg-transparent p-0 text-teal-400 hover:underline">
              Kodu tekrar gönder
            </button>
          ))}
      </div>
    </Card>
  );
}

function NoAccess({ onRetry }: { onRetry: () => void }) {
  return (
    <Card>
      <Eyebrow>YETKİ YOK</Eyebrow>
      <div className="flex items-start gap-3.5">
        <span className="grid size-11 flex-none animate-shake place-items-center rounded-[13px] bg-red-400/18 text-[#EE9191] [animation-delay:700ms]">
          <Lock size={22} aria-hidden="true" />
        </span>
        <div>
          <h2 className="mb-1.5 font-heading text-[22px] font-bold">NovaChat erişimin yok</h2>
          <p className="m-0 text-sm leading-normal text-fg-2">
            Hesabın doğrulandı ama NovaChat yetkin tanımlı değil. Yöneticinden Novadan admin panelinde sana şu rolü vermesini iste:
          </p>
        </div>
      </div>
      <p className="m-0 flex flex-wrap items-center gap-2 text-sm text-fg-2">
        <span className="font-semibold text-fg-1">Novachat Giriş Yapma</span>
        <code className="rounded-sm border border-line bg-surface-2 px-2.5 py-1.5 font-mono text-[13px] text-brand-300">chat-login</code>
      </p>
      <button type="button" onClick={onRetry} className="nc-press mt-auto h-12 rounded-md border border-line-strong bg-surface-2 text-[15px] font-semibold">
        Farklı hesapla gir
      </button>
    </Card>
  );
}
